import React from 'react';
import PropTypes from 'prop-types';
import { LuBedDouble, LuCircleDot, LuClock3, LuLogIn, LuTrophy, LuUsersRound } from 'react-icons/lu';
import StatTiles from '../../../components/data/StatTiles';
import useAccessText from '../accessText';
import { fullName, isActiveEmployee } from '../accessUtils';
import { PassiveTag, PersonAvatar } from './accessBits';

const PANEL = 'tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03]';
const PANEL_TITLE = 'tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100';

const RankRow = ({ row, order, meta }) => (
  <li className="tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-px-2 tw-py-2">
    {order !== undefined && <span className="tw-w-5 tw-shrink-0 tw-text-center tw-text-sm tw-font-bold tw-tabular-nums tw-text-slate-400">{order}</span>}
    <PersonAvatar employee={row.emp} online={row.isOnline ? true : undefined} passive={!isActiveEmployee(row.emp)} size="avatar" />
    <span className="tw-min-w-0 tw-flex-1">
      <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
        <span className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{fullName(row.emp)}</span>
        {!isActiveEmployee(row.emp) && <PassiveTag />}
      </span>
      <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{meta}</span>
    </span>
  </li>
);

RankRow.propTypes = { row: PropTypes.object.isRequired, order: PropTypes.number, meta: PropTypes.string };

/** Tiles, the daily sign-in chart and the two rankings. */
const UsageOverview = ({ summary, total, chart, chartDays, topActive, leastActive }) => {
  const { label, formatDuration, formatRelative } = useAccessText();
  const tiles = [
    { key: 'online', label: label('stats.online'), shortLabel: label('stats.onlineShort'), value: summary.onlineCount, icon: LuCircleDot, gradient: 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30' },
    { key: 'active', label: label('stats.active30'), shortLabel: label('stats.active30Short'), value: summary.active30Count, suffix: `/ ${total}`, icon: LuUsersRound, gradient: 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', share: total > 0 ? summary.active30Count / total : 0, barClass: 'tw-bg-gradient-to-r tw-from-sky-300 tw-to-blue-500' },
    { key: 'logins', label: label('stats.logins30'), shortLabel: label('stats.logins30Short'), value: summary.totalLogins30, icon: LuLogIn, gradient: 'tw-from-violet-400 tw-to-purple-600 tw-shadow-violet-500/30' },
    { key: 'time', label: label('stats.totalOnline'), shortLabel: label('stats.totalOnlineShort'), value: summary.totalOnlineMs, format: formatDuration, icon: LuClock3, gradient: 'tw-from-amber-400 tw-to-orange-500 tw-shadow-amber-500/30' },
  ];

  return (
    <div className="tw-space-y-4">
      <StatTiles tiles={tiles} ready className="tw-grid-cols-2 lg:tw-grid-cols-4" />

      <section className={PANEL}>
        <p className={PANEL_TITLE}>{label('stats.chartTitle', { days: chartDays })}</p>
        <div className="tw-mt-4 tw-flex tw-h-48 tw-items-end tw-gap-1.5 sm:tw-gap-2">
          {chart.days.map((day) => (
            <div key={day.start} title={label('stats.chartBar', { date: day.label, count: day.count })} className="tw-flex tw-h-full tw-min-w-0 tw-flex-1 tw-flex-col tw-items-center tw-gap-1">
              <span className="tw-h-4 tw-text-[11px] tw-font-semibold tw-tabular-nums tw-text-slate-600 dark:tw-text-slate-300">{day.count > 0 ? day.count : ''}</span>
              <div className="tw-flex tw-w-full tw-flex-1 tw-items-end tw-rounded-lg tw-bg-slate-100 dark:tw-bg-white/[0.05]">
                <div
                  style={{ height: `${(day.count / chart.max) * 100}%` }}
                  className="tw-w-full tw-rounded-lg tw-bg-gradient-to-t tw-from-teal-500 tw-to-cyan-400 tw-transition-[height] tw-duration-500"
                />
              </div>
              <span className="tw-text-[10px] tw-font-medium tw-tabular-nums tw-text-slate-500 dark:tw-text-slate-400">{day.label}</span>
              <span className="tw-hidden tw-text-[10px] tw-text-slate-400 sm:tw-block dark:tw-text-slate-500">{day.weekday}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="tw-grid tw-gap-4 md:tw-grid-cols-2">
        <section className={PANEL}>
          <p className={PANEL_TITLE}><LuTrophy aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-amber-500" />{label('stats.top')}</p>
          {topActive.length === 0 ? (
            <p className="tw-mt-3 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('stats.topEmpty')}</p>
          ) : (
            <ol className="tw-mt-2">
              {topActive.map((row, index) => (
                <RankRow key={row.emp.id} row={row} order={index + 1} meta={label('stats.topMeta', { logins: row.logins30, duration: formatDuration(row.totalOnlineMs) })} />
              ))}
            </ol>
          )}
        </section>
        <section className={PANEL}>
          <p className={PANEL_TITLE}><LuBedDouble aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />{label('stats.least')}</p>
          {leastActive.length === 0 ? (
            <p className="tw-mt-3 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{label('stats.leastEmpty')}</p>
          ) : (
            <ul className="tw-mt-2">
              {leastActive.map((row) => (
                <RankRow key={row.emp.id} row={row} meta={label('stats.lastActivity', { time: formatRelative(row.lastActivity) })} />
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

UsageOverview.propTypes = {
  summary: PropTypes.object.isRequired,
  total: PropTypes.number.isRequired,
  chart: PropTypes.shape({ days: PropTypes.array.isRequired, max: PropTypes.number.isRequired }).isRequired,
  chartDays: PropTypes.number.isRequired,
  topActive: PropTypes.arrayOf(PropTypes.object).isRequired,
  leastActive: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default UsageOverview;
